import { useEffect, useMemo, useRef } from 'react';

import { WebSocketManager } from '@/services/websocket/WebSocketManager';
import { WebSocketEventListener, WebSocketEventType } from '@/types/websocket';

export function useWebSocketManager(token: string | null) {
  // Create a singleton WebSocket manager instance
  const wsManager = useMemo(() => {
    // Use a static variable to ensure single instance across hook calls
    if (typeof window !== 'undefined' && !(window as any).wsManagerInstance) {
      console.log(
        '[useWebSocketManager] Creating new WebSocket manager instance',
      );
      (window as any).wsManagerInstance = new WebSocketManager(
        process.env.NEXT_PUBLIC_WS_URL || '',
      );
    }
    if (typeof window === 'undefined') {
      return null;
    }
    return (window as any).wsManagerInstance;
  }, []); // Empty deps array ensures single instance

  // Keep track of subscriptions for cleanup
  const subscriptions = useRef<(() => void)[]>([]);

  // Set token and handle connection
  useEffect(() => {
    if (!wsManager) return undefined;

    wsManager.setToken(token);

    return () => {
      if (wsManager) {
        // Only disconnect if no other components are using the WebSocket
        if (document.querySelectorAll('[data-ws-connected]').length <= 1) {
          wsManager.disconnect();
        }
      }
    };
  }, [token, wsManager]);

  // Helper function to subscribe to events with automatic cleanup
  const subscribe = <T extends WebSocketEventType>(
    event: T,
    listener: WebSocketEventListener<T>,
  ): (() => void) => {
    if (!wsManager) {
      console.warn('WebSocketManager not initialized');
      return () => {};
    }
    const unsubscribe = wsManager.on(event, listener);
    console.log('[useWebSocketManager] Subscribed to event', event);
    subscriptions.current.push(unsubscribe);
    return unsubscribe;
  };

  // Clean up subscriptions on unmount
  useEffect(() => {
    return () => {
      subscriptions.current.forEach((unsubscribe) => unsubscribe());
      subscriptions.current = [];
    };
  }, []);

  const emit = (message: any): void => {
    if (wsManager?.emit) {
      wsManager.emit(message);
      return;
    }
    console.warn('WebSocket not initialized');
  };

  return {
    subscribe,
    emit,
    disconnect: () => wsManager?.disconnect?.(),
    connect: () => wsManager?.connect?.(),
    loadedListeners: wsManager?.loadedListeners ?? [],
  };
}
